import { buildQuery } from '@/utils/helpers'
import fetcher, { fetchWithToken } from '../../../services/fetcher'

// ========================================
// GraphQL Queries & Mutations
// ========================================

const GET_ORDER_ITEMS_FOR_REVIEW = `
query GetOrderItemsForReview($increment_id: String!, $protect_code: String!) {
  getOrderItemsForReview(increment_id: $increment_id, protect_code: $protect_code) {
    increment_id
    order_id
    order_status
    refund_amount
    refund_percentage
    items_to_review {
      product_name
      product_sku
      product_image {
        disabled
        label
        position
        url
      }
      qty_ordered
      options {
        label
        option_id
        option_value
        value
      }
    }
  }
}`

const SUBMIT_ORDER_REVIEW = `
mutation SubmitOrderReview($input: SubmitOrderReviewInput!) {
  submitOrderReview(input: $input) {
    review_id
    status
    verification_data {
      email
    }
    verification_token
  }
}`

const VERIFY_ORDER_REVIEW_OTP = `
mutation VerifyOrderReviewOtp($input: VerifyOrderReviewInput!) {
  verifyOrderReviewOtp(input: $input) {
    message
    review_id
    status
    verification_data {
      email
      nickname
      telephone
    }
    token
  }
}`

// ========================================
// TypeScript Interfaces
// ========================================

export interface IProductImage {
  disabled: boolean | null
  label: string
  position: number | null
  url: string
}

export interface IProductOption {
  label: string
  option_id: string
  option_value: string
  value: string
}

export interface IItemToReview {
  product_name: string
  product_sku: string
  product_image: IProductImage
  qty_ordered: number
  options: IProductOption[]
}

export interface IOrderItemsForReview {
  increment_id: string
  order_id: number
  order_status: string
  refund_amount: number
  refund_percentage: number
  items_to_review: IItemToReview[]
}

export interface ISubmitOrderReviewInput {
  increment_id: string
  protect_code: string
  description: string
  rating_stars: number
  images?: string[]
  videos?: string[]
  refund_amount: string
}

export interface IVerificationData {
  email?: string
  nickname?: string
  telephone?: string
}

export interface ISubmitOrderReviewResponse {
  review_id: number | null
  status: 'SUCCESS' | 'OTP_REQUIRED'
  verification_data: IVerificationData | null
  verification_token: string | null
}

export interface IVerifyOrderReviewOtpInput {
  otp: string
  verification_token: string
}

export interface IVerifyOrderReviewOtpResponse {
  message: string
  review_id: number
  status: 'SUCCESS'
  verification_data: IVerificationData
  token: string
}

// ========================================
// API Service Functions
// ========================================

/**
 * Lấy danh sách sản phẩm trong đơn hàng cần review
 * @param increment_id - Mã đơn hàng (VD: HYDRA000097)
 * @param protect_code - Mã bảo vệ (có thể để trống "")
 */
export const getOrderItemsForReview = async (params: {
  increment_id: string
  protect_code: string
}): Promise<{ status: string; data?: IOrderItemsForReview; message?: string; isLoginRequired: boolean }> => {
  try {
    const response = await fetcher.get(buildQuery(GET_ORDER_ITEMS_FOR_REVIEW, params))

    const data = response?.payload?.data?.getOrderItemsForReview

    if (response?.payload?.errors) {
      const isUnauthorized = response.payload.errors[0]?.extensions?.category === 'graphql-authorization'
      return {
        status: 'error',
        message: response.payload.errors[0]?.message || 'Không thể lấy thông tin đơn hàng',
        isLoginRequired: isUnauthorized,
      }
    }

    if (!data) {
      return {
        status: 'error',
        message: 'Không tìm thấy đơn hàng',
        isLoginRequired: false,
      }
    }

    // Kiểm tra trạng thái đơn hàng
    if (data.order_status !== 'complete') {
      return {
        status: 'error',
        message: 'Đơn hàng chưa hoàn thành. Chỉ có thể đánh giá đơn hàng đã hoàn thành.',
        isLoginRequired: false,
      }
    }

    return {
      status: 'success',
      data,
      isLoginRequired: false,
    }
  } catch (error) {
    console.error('Error fetching order items for review:', error)
    return {
      status: 'error',
      message: 'Có lỗi xảy ra khi lấy thông tin đơn hàng',
      isLoginRequired: false,
    }
  }
}

export const getOrderItemsForReviewWithToken = async (params: {
  increment_id: string
  protect_code: string
}): Promise<{ status: string; data?: IOrderItemsForReview; message?: string; isLoginRequired: boolean }> => {
  try {
    const response = await fetchWithToken(
      {
        query: GET_ORDER_ITEMS_FOR_REVIEW,
        variables: params,
      },
      {
        next: { tags: ['orders'] },
        cache: 'force-cache',
      }
    ).then((r) => r?.payload)
    const data = response?.data?.getOrderItemsForReview
    if (response?.errors) {
      const isUnauthorized = response.payload.errors[0]?.extensions?.category === 'graphql-authorization'
      return {
        status: 'error',
        message: response.payload.errors[0]?.message || 'Không thể lấy thông tin đơn hàng',
        isLoginRequired: isUnauthorized,
      }
    }

    if (!data) {
      return {
        status: 'error',
        message: 'Không tìm thấy đơn hàng',
        isLoginRequired: false,
      }
    }

    // Kiểm tra trạng thái đơn hàng
    if (data.order_status !== 'complete') {
      return {
        status: 'error',
        message: 'Đơn hàng chưa hoàn thành. Chỉ có thể đánh giá đơn hàng đã hoàn thành.',
        isLoginRequired: false,
      }
    }

    return {
      status: 'success',
      data,
      isLoginRequired: false,
    }
  } catch (error) {
    console.error('Error fetching order items for review:', error)
    return {
      status: 'error',
      message: 'Có lỗi xảy ra khi lấy thông tin đơn hàng',
      isLoginRequired: false,
    }
  }
}

/**
 * Gửi đánh giá đơn hàng
 * @param input - Dữ liệu đánh giá
 */
export const submitOrderReview = async (
  input: ISubmitOrderReviewInput
): Promise<{ status: string; data?: ISubmitOrderReviewResponse; message?: string }> => {
  try {
    const response = await fetchWithToken({
      query: SUBMIT_ORDER_REVIEW,
      variables: { input },
    })

    const data = response?.payload?.data?.submitOrderReview

    if (response?.payload?.errors) {
      return {
        status: 'error',
        message: response.payload.errors[0]?.message || 'Không thể gửi đánh giá',
      }
    }

    if (!data) {
      return {
        status: 'error',
        message: 'Không nhận được phản hồi từ server',
      }
    }

    return {
      status: 'success',
      data,
    }
  } catch (error) {
    console.error('Error submitting order review:', error)
    return {
      status: 'error',
      message: 'Có lỗi xảy ra khi gửi đánh giá',
    }
  }
}

/**
 * Xác thực OTP cho đánh giá đơn hàng (Guest checkout)
 * @param input - OTP và verification token
 */
export const verifyOrderReviewOtp = async (
  input: IVerifyOrderReviewOtpInput
): Promise<{ status: string; data?: IVerifyOrderReviewOtpResponse; message?: string }> => {
  try {
    // const query = buildQuery(VERIFY_ORDER_REVIEW_OTP, { input })
    const response = await fetchWithToken({
      query: VERIFY_ORDER_REVIEW_OTP,
      variables: { input },
    })

    const data = response?.payload?.data?.verifyOrderReviewOtp

    if (response?.payload?.errors) {
      return {
        status: 'error',
        message: response.payload.errors[0]?.message || 'Mã OTP không hợp lệ',
      }
    }

    if (!data) {
      return {
        status: 'error',
        message: 'Không nhận được phản hồi từ server',
      }
    }

    if (data.status !== 'SUCCESS') {
      return {
        status: 'error',
        message: data.message || 'Xác thực OTP thất bại',
      }
    }

    return {
      status: 'success',
      data,
    }
  } catch (error) {
    console.error('Error verifying OTP:', error)
    return {
      status: 'error',
      message: 'Có lỗi xảy ra khi xác thực OTP',
    }
  }
}
